import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react"

import { useAuth } from "src/context/AuthContext"
import {
  clearLastShownSplashSponsorId,
  getLastShownSplashSponsorId,
  setLastShownSplashSponsorId,
} from "src/context/channelSplashMemory"
import { useChannelBrandQuery } from "src/queries/useChannelBrand"
import { ChannelBrandData } from "src/services/api/api.types"

interface ChannelBrandContextValue {
  brandData: ChannelBrandData | null
  isLoading: boolean
  shouldShowSplash: boolean
  markSplashShown: () => void
}

export const ChannelBrandContext = createContext<ChannelBrandContextValue | null>(null)

/**
 * Provides channel brand data and controls the channel splash screen lifecycle.
 *
 * Splash logic:
 * - shouldShowSplash is initialized synchronously to true on mount (lazy useState)
 *   so the splash blocks the chat screen from the very first render, eliminating flash.
 * - useLayoutEffect (fires before paint) handles subsequent channel switches the same way.
 * - The splash itself shows for 1 second with whatever brand data has loaded by then;
 *   if data is still loading it shows a white screen, satisfying the "slow network:
 *   proceed to enter channel without waiting" requirement.
 */
export function ChannelBrandProvider({ children }: { children: React.ReactNode }) {
  const { sponsorId, isAuthenticated } = useAuth()
  const brandQuery = useChannelBrandQuery()

  // Track which sponsorId we last showed a splash for in this mounted provider.
  const lastSplashSponsorIdRef = useRef<string | null>(getLastShownSplashSponsorId())

  // Initialize to true synchronously so the splash is present on the very first render,
  // preventing the chat screen from flashing before the splash appears on login.
  // If this sponsor was already shown, keep it hidden to avoid remount flicker.
  const [shouldShowSplash, setShouldShowSplash] = useState<boolean>(
    () =>
      !!sponsorId &&
      sponsorId !== "unknown-sponsor" &&
      sponsorId !== lastSplashSponsorIdRef.current,
  )

  // useLayoutEffect fires synchronously after layout, before the screen is painted.
  // This prevents a flash of the chat screen during channel switches as well.
  useLayoutEffect(() => {
    if (!sponsorId || sponsorId === "unknown-sponsor") return
    if (sponsorId === lastSplashSponsorIdRef.current) return
    // Mark immediately so remounts during transition don't retrigger splash.
    lastSplashSponsorIdRef.current = sponsorId
    setLastShownSplashSponsorId(sponsorId)
    setShouldShowSplash(true)
  }, [sponsorId])

  // Clear splash memory only when the auth session ends (logout).
  // During channel switching sponsorId can be transiently unknown while still authenticated.
  useEffect(() => {
    if (isAuthenticated) return
    lastSplashSponsorIdRef.current = null
    clearLastShownSplashSponsorId()
    setShouldShowSplash(false)
  }, [isAuthenticated])

  const markSplashShown = useCallback(() => {
    const normalizedSponsorId =
      sponsorId && sponsorId !== "unknown-sponsor" ? sponsorId : lastSplashSponsorIdRef.current
    lastSplashSponsorIdRef.current = normalizedSponsorId
    setLastShownSplashSponsorId(normalizedSponsorId)
    setShouldShowSplash(false)
  }, [sponsorId])

  return (
    <ChannelBrandContext.Provider
      value={{
        brandData: brandQuery.data ?? null,
        isLoading: brandQuery.isLoading,
        shouldShowSplash,
        markSplashShown,
      }}
    >
      {children}
    </ChannelBrandContext.Provider>
  )
}

export function useChannelBrand() {
  const context = useContext(ChannelBrandContext)
  if (!context) {
    throw new Error("useChannelBrand must be used within ChannelBrandProvider")
  }
  return context
}
